import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, createDeck, shuffleDeck } from "./Deck";
import CardView from "./CardView";
import {
  findOuts,
  calculateEquity,
  OutsResult,
  EquityResult,
  outGroupHitProbability,
  formatCard,
} from "./evaluator";
import InfoTooltip from "./InfoTooltip";
import FormulaDisplay, {
  FormulaData,
  RunnerRunnerFormula,
} from "./FormulaDisplay";

const EQUITY_EXPLANATION =
  "Equity is the percentage of the time your hand would beat a completely random opponent hand. It tests every possible 2-card combination an opponent could hold and checks who wins — giving you an exact win rate assuming you have no reads on your opponent.";

interface Question {
  holeCards: Card[];
  communityCards: Card[];
  /** For 3/4 community cards: which out group are we asking about */
  targetOutGroup?: {
    name: string;
    count: number;
    cards: Card[];
    exactPercent: number;
  };
  /** For 5 community cards: equity question */
  isEquityQuestion: boolean;
}

function generateQuestion(): Question {
  const deck = shuffleDeck(createDeck());
  const holeCards = [deck[0], deck[1]];
  const communityCount = [3, 4, 5][Math.floor(Math.random() * 3)];
  const communityCards = deck.slice(2, 2 + communityCount);

  if (communityCount === 5) {
    return { holeCards, communityCards, isEquityQuestion: true };
  }

  // Find outs and pick a random out group to ask about
  const outsResult = findOuts(holeCards, communityCards);

  if (outsResult.outGroups.length === 0) {
    // No outs — regenerate a new hand
    return generateQuestion();
  }

  const group =
    outsResult.outGroups[
      Math.floor(Math.random() * outsResult.outGroups.length)
    ];
  const exactPercent = outGroupHitProbability(
    group.count,
    outsResult.remainingCount,
    outsResult.cardsToCome
  );

  return {
    holeCards,
    communityCards,
    targetOutGroup: {
      name: group.improvementName,
      count: group.count,
      cards: group.cards,
      exactPercent,
    },
    isEquityQuestion: false,
  };
}

const GuessMode: React.FC = () => {
  const [question, setQuestion] = useState<Question>(generateQuestion);
  const [guess, setGuess] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [result, setResult] = useState<{
    actual: number;
    outsInfo?: OutsResult;
    equity?: EquityResult;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const focusInput = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    if (!revealed) {
      focusInput();
    }
  }, [question, revealed, focusInput]);

  useEffect(() => {
    if (revealed) {
      setTimeout(() => nextButtonRef.current?.focus(), 50);
    }
  }, [revealed]);

  const handleReveal = useCallback(() => {
    if (revealed) return;
    const { holeCards, communityCards, targetOutGroup, isEquityQuestion } =
      question;

    if (isEquityQuestion) {
      const equity = calculateEquity(holeCards, communityCards);
      setResult({ actual: equity.winPercent, equity });
    } else {
      const outsInfo = findOuts(holeCards, communityCards);
      setResult({ actual: targetOutGroup!.exactPercent, outsInfo });
    }
    setRevealed(true);
  }, [revealed, question]);

  const handleNext = useCallback(() => {
    setQuestion(generateQuestion());
    setGuess("");
    setRevealed(false);
    setResult(null);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!revealed) {
        handleReveal();
      }
    }
  };

  const guessNum = parseFloat(guess);
  const diff = result ? Math.abs(guessNum - result.actual) : 0;

  const getDiffColor = () => {
    if (!result || isNaN(guessNum)) return "text-gray-400";
    if (diff <= 2) return "text-green-400";
    if (diff <= 5) return "text-yellow-400";
    if (diff <= 10) return "text-orange-400";
    return "text-red-400";
  };

  const getDiffLabel = () => {
    if (isNaN(guessNum)) return "No guess";
    if (diff <= 2) return "Excellent!";
    if (diff <= 5) return "Close!";
    if (diff <= 10) return "Not bad";
    return "Keep practicing";
  };

  return (
    <div className="flex flex-col items-center gap-5">
      {/* Your hand */}
      <div>
        <div className="text-gray-400 text-xs uppercase tracking-wider mb-2 text-center">
          Your Hand
        </div>
        <div className="flex gap-2 justify-center">
          {question.holeCards.map((c, i) => (
            <CardView key={i} card={c} size="lg" />
          ))}
        </div>
      </div>

      {/* Community cards */}
      <div>
        <div className="text-gray-400 text-xs uppercase tracking-wider mb-2 text-center">
          {question.communityCards.length === 3
            ? "Flop"
            : question.communityCards.length === 4
            ? "Turn"
            : "River"}
        </div>
        <div className="flex gap-2 justify-center">
          {question.communityCards.map((c, i) => (
            <CardView key={i} card={c} size="lg" />
          ))}
        </div>
      </div>

      {/* Question */}
      <div className="text-center px-4">
        {question.isEquityQuestion ? (
          <p className="text-white text-base inline-flex items-center justify-center flex-wrap">
            What's your{" "}
            <span className="text-amber-400 font-semibold mx-1">equity</span>
            vs. a random hand?
            <InfoTooltip text={EQUITY_EXPLANATION} />
          </p>
        ) : (
          <p className="text-white text-base">
            Probability of making a{" "}
            <span className="text-amber-400 font-semibold">
              {question.targetOutGroup!.name}
            </span>
            ?
          </p>
        )}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2">
        <div className="relative">
          <input
            ref={inputRef}
            type="number"
            inputMode="decimal"
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={revealed}
            placeholder="0"
            className="w-24 h-12 bg-gray-800 border border-gray-600 rounded-lg text-white text-center text-xl
              focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/50
              disabled:opacity-60 placeholder-gray-600
              [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
            %
          </span>
        </div>
        {!revealed ? (
          <button
            onClick={handleReveal}
            className="h-12 px-5 bg-amber-500 hover:bg-amber-400 active:scale-95 text-gray-900 font-semibold rounded-lg transition-all"
          >
            Reveal
          </button>
        ) : (
          <button
            ref={nextButtonRef}
            onClick={handleNext}
            className="h-12 px-5 bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-semibold rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            Next →
          </button>
        )}
      </div>

      {/* Result */}
      {revealed && result && (
        <div className="w-full max-w-sm bg-gray-800/80 rounded-xl p-4 border border-gray-700 space-y-3">
          {/* Actual vs guess */}
          <div className="text-center space-y-1">
            <div className="text-2xl font-bold text-white">
              {result.actual}%
            </div>
            <div className={`text-sm font-medium ${getDiffColor()}`}>
              {getDiffLabel()}
              {!isNaN(guessNum) && (
                <span className="text-gray-500 ml-1">
                  (off by {diff.toFixed(1)}%)
                </span>
              )}
            </div>
          </div>

          {/* Outs detail */}
          {result.outsInfo && (
            <div className="space-y-3 pt-2 border-t border-gray-700">
              <div className="text-gray-400 text-xs">
                Current hand:{" "}
                <span className="text-white">
                  {result.outsInfo.currentHand.name}
                </span>
              </div>

              {/* Per-group breakdown */}
              {result.outsInfo.outGroups.map((g) => {
                const formulaData: FormulaData = {
                  outs: g.count,
                  nonOuts: result.outsInfo!.remainingCount - g.count,
                  remaining: result.outsInfo!.remainingCount,
                  cardsToCome: result.outsInfo!.cardsToCome,
                  percent: g.hitPercent,
                };
                return (
                  <div
                    key={g.improvementName}
                    className="space-y-1.5 bg-gray-900/50 rounded-lg p-2.5"
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-white font-medium">
                        {g.improvementName}
                      </span>
                      <span className="text-xs font-semibold text-amber-400">
                        {g.hitPercent}%
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {g.cards.map((c) => (
                        <span
                          key={formatCard(c)}
                          className="text-xs font-mono bg-gray-700 px-1.5 py-0.5 rounded"
                          style={{
                            color:
                              c.suit === "h" || c.suit === "d"
                                ? "#ef4444"
                                : "#e5e7eb",
                          }}
                        >
                          {formatCard(c)}
                        </span>
                      ))}
                      <span className="text-[10px] text-gray-500 self-center ml-1">
                        {g.count} out{g.count !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="pl-1">
                      <FormulaDisplay data={formulaData} />
                    </div>
                  </div>
                );
              })}

              {/* Total improvement odds */}
              {result.outsInfo.outGroups.length > 1 && (
                <div className="space-y-1.5 pt-2 border-t border-gray-700/50">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs text-gray-400">
                      Any improvement ({result.outsInfo.totalOuts} outs ·{" "}
                      {result.outsInfo.cardsToCome} card
                      {result.outsInfo.cardsToCome !== 1 ? "s" : ""} to come)
                    </span>
                    <span className="text-xs font-semibold text-amber-400">
                      {result.outsInfo.hitAnyPercent}%
                    </span>
                  </div>
                  <div className="pl-1">
                    <FormulaDisplay
                      data={{
                        outs: result.outsInfo.totalOuts,
                        nonOuts:
                          result.outsInfo.remainingCount -
                          result.outsInfo.totalOuts,
                        remaining: result.outsInfo.remainingCount,
                        cardsToCome: result.outsInfo.cardsToCome,
                        percent: result.outsInfo.hitAnyPercent,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Runner-runner groups */}
              {result.outsInfo.runnerRunnerGroups.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-gray-700/50">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">
                    Runner-runner (needs both cards)
                  </div>
                  {result.outsInfo.runnerRunnerGroups.map((rr) => (
                    <div
                      key={rr.improvementName}
                      className="space-y-1.5 bg-gray-900/30 rounded-lg p-2.5 border border-gray-700/30"
                    >
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs text-white font-medium">
                          {rr.improvementName}
                        </span>
                        <span className="text-xs font-semibold text-amber-400/70">
                          {rr.hitPercent}%
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {rr.comboCount} combo{rr.comboCount !== 1 ? "s" : ""}{" "}
                        out of {rr.totalCombos}
                      </div>
                      <div className="pl-1">
                        <RunnerRunnerFormula
                          data={{
                            comboCount: rr.comboCount,
                            totalCombos: rr.totalCombos,
                            remaining: result.outsInfo!.remainingCount,
                            percent: rr.hitPercent,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Total including runner-runner */}
              {result.outsInfo.runnerRunnerGroups.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-gray-700/50">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs text-gray-400 font-medium">
                      Total (outs + runner-runner)
                    </span>
                    <span className="text-xs font-bold text-amber-400">
                      {result.outsInfo.hitAnyIncludingRRPercent}%
                    </span>
                  </div>
                  <div className="pl-1">
                    <RunnerRunnerFormula
                      data={{
                        comboCount: result.outsInfo.totalImprovingCombos,
                        totalCombos: result.outsInfo.totalPossibleCombos,
                        remaining: result.outsInfo.remainingCount,
                        percent: result.outsInfo.hitAnyIncludingRRPercent,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Equity detail */}
          {result.equity && (
            <div className="space-y-1 pt-2 border-t border-gray-700">
              <div className="text-gray-400 text-xs inline-flex items-center justify-center w-full">
                Hand:{" "}
                <span className="text-white ml-1">
                  {result.equity.currentHand}
                </span>
                <InfoTooltip text={EQUITY_EXPLANATION} />
              </div>
              <div className="flex justify-center gap-4 text-xs">
                <span className="text-green-400">
                  Win {result.equity.winPercent}%
                </span>
                <span className="text-gray-400">
                  Tie {result.equity.tiePercent}%
                </span>
                <span className="text-red-400">
                  Lose {result.equity.losePercent}%
                </span>
              </div>
              <div className="text-gray-500 text-xs text-center">
                {result.equity.totalMatchups.toLocaleString()} matchups
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GuessMode;
